import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { Heart, ArrowsClockwise, Quotes, ImageSquare } from "@phosphor-icons/react";

const KIND_ORDER = ["Driving Force", "If This Works", "What They've Missed", "What's Stopping Them"];
const KIND_META = {
  "Driving Force": { color: "#FF3333", tag: "The WHY" },
  "If This Works": { color: "#FF9933", tag: "The Future" },
  "What They've Missed": { color: "#9933FF", tag: "The Cost" },
  "What's Stopping Them": { color: "#33CCFF", tag: "The Block" },
};

/**
 * CoachMotivationWall — anonymised screenshot vault of client narratives from
 * onboarding. Each quote card is styled square-ish to look great as an IG post.
 * Toggle "Anonymous" mode strips the initial + founder number for full anonymity.
 */
export default function CoachMotivationWall() {
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [kindFilter, setKindFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/coach/motivation-wall");
      setQuotes(data.quotes || []);
      setTotal(data.count || 0);
    } catch (e) { setErr(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = kindFilter === "all" ? quotes : quotes.filter((q) => q.kind === kindFilter);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="motivation-wall">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="id-label mb-1 flex items-center gap-2 text-[#FF3333]">
            <Heart size={12} weight="fill" /> — Motivation Wall —
          </div>
          <h1 className="id-h1">Your Clients&apos; WHY</h1>
          <p className="text-zinc-400 font-manrope text-sm mt-1 max-w-2xl leading-relaxed">
            Every narrative answer your clients wrote at onboarding. Screenshot any quote for Instagram / ads / reels.
            Toggle <span className="text-white">Anonymous mode</span> before you share.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs font-manrope cursor-pointer">
            <input
              type="checkbox"
              checked={anonymous}
              onChange={(e) => setAnonymous(e.target.checked)}
              className="accent-[#FF3333]"
              data-testid="anon-toggle"
            />
            <span className={anonymous ? "text-[#FF3333]" : "text-zinc-300"}>Anonymous</span>
          </label>
          <button onClick={load} className="id-btn-ghost text-xs" data-testid="refresh">
            <ArrowsClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="kind-filter">
        <FilterChip active={kindFilter === "all"} onClick={() => setKindFilter("all")} label={`All (${total})`} />
        {KIND_ORDER.map((k) => {
          const n = quotes.filter((q) => q.kind === k).length;
          return (
            <FilterChip
              key={k}
              active={kindFilter === k}
              onClick={() => setKindFilter(k)}
              label={`${k} (${n})`}
              color={KIND_META[k].color}
            />
          );
        })}
      </div>

      {err && <div className="border border-[#FF3333]/60 bg-[#FF3333]/10 text-[#FF9999] px-3 py-2 text-xs font-manrope">{err}</div>}
      {loading && <div className="id-label text-zinc-500 id-pulse">Loading quotes…</div>}

      {!loading && filtered.length === 0 && (
        <div className="id-card text-center py-12" data-testid="empty-state">
          <Quotes size={40} weight="thin" className="mx-auto mb-3 text-zinc-700" />
          <p className="text-zinc-400 font-manrope">
            {total === 0 ? "No client onboarding narratives yet. Once your founders sign up + fill out their WHY, quotes appear here." : "No quotes in this category."}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="quote-grid">
        {filtered.map((q) => (
          <QuoteCard key={q.id} q={q} anonymous={anonymous} />
        ))}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, color }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 border text-xs font-manrope uppercase tracking-[0.1em] transition-colors ${
        active
          ? "border-white bg-white text-black"
          : "border-[#262626] text-zinc-400 hover:border-zinc-500"
      }`}
      style={active && color ? { borderColor: color, background: color, color: "#000" } : undefined}
      data-testid={`chip-${label.replace(/[^a-z]/gi, "").toLowerCase()}`}
    >
      {label}
    </button>
  );
}

function QuoteCard({ q, anonymous }) {
  const meta = KIND_META[q.kind] || {};
  return (
    <div
      className="border p-6 flex flex-col justify-between min-h-[280px] bg-gradient-to-br from-[#0F0F0F] to-[#0A0A0A]"
      style={{ borderColor: meta.color + "66" }}
      data-testid={`quote-card-${q.id}`}
    >
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="id-label uppercase tracking-[0.15em]" style={{ color: meta.color }}>
            — {meta.tag || "Quote"} —
          </div>
          <Quotes size={20} weight="fill" style={{ color: meta.color, opacity: 0.4 }} />
        </div>
        <p className="text-white font-manrope text-base leading-relaxed whitespace-pre-wrap">
          &ldquo;{q.quote}&rdquo;
        </p>
      </div>
      <div className="mt-6 pt-4 border-t border-[#262626] flex items-center justify-between">
        <div className="text-xs font-manrope text-zinc-500">
          {anonymous ? (
            <span className="text-zinc-600">— Anonymous client —</span>
          ) : (
            <>
              <span className="text-zinc-300 font-semibold">{q.initial || "—"}</span>
              {q.founder_number ? <span className="text-[#FF3333] ml-2">· Founder #{q.founder_number}</span> : null}
            </>
          )}
        </div>
        <div className="text-[10px] font-manrope text-zinc-600 uppercase tracking-[0.1em] flex items-center gap-1">
          <ImageSquare size={10} weight="bold" /> Screenshot ready
        </div>
      </div>
    </div>
  );
}
